import { AccessibilityHelper } from "../GlobalAssets/js/accessibilityHelper";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";

class ServicesListings {
  constructor(component) {
    this.refs = {
      accordion: '.js-accordion',
      accordionToggle: '.js-accordion-toggle',
      accordionContent: '.js-accordion-content',
      accordionIcon: '.js-accordion-icon',
      navToggle: '.js-nav-toggle',
      navSelected: '.js-nav-selected',
      navToggleIcon: '.js-nav-toggle-icon',
      navList: '.js-nav-list',
      navItem: '.js-nav-item',
      navLinks: '.js-nav-link',
      serviceListing: '.js-service-listing',
    };
    
    this.dom = {
      $component: component,
      $accordions: component.querySelectorAll(this.refs.accordion),
      $navToggle: component.querySelector(this.refs.navToggle),
      $navSelected: component.querySelector(this.refs.navSelected),
      $navToggleIcon: component.querySelector(this.refs.navToggleIcon),
      $navList: component.querySelector(this.refs.navList),
      $navItems: component.querySelectorAll(this.refs.navItem),
      $navLinks: component.querySelectorAll(this.refs.navLinks),
      $serviceListings: component.querySelectorAll(this.refs.serviceListing),
    }

    this.state = {
      accessibilityHelper: new AccessibilityHelper(),
      isMobile: null,
    }

    this.mount();

    this.init();
  }

  mount() {
    const { dom, refs, state } = this;
    if (dom.$accordions) {
      dom.$accordions.forEach($el => {
        $el.addEventListener('click', this.handleAccordionClick.bind(this));
      });
    }

    if (dom.$navToggle) {
      dom.$navToggle.addEventListener('click', this.toggleNav.bind(this));
    }

    if (dom.$navLinks) {
      dom.$navLinks.forEach($link => {
        $link.addEventListener('click', this.handleNavLinkClick.bind(this));
      });
    }

    window.addEventListener('resize', () => {
      state.isMobile = window.innerWidth <= 768;
    });
  }

  init() {
    const { dom, state } = this;

    state.isMobile = window.innerWidth <= 768;

    gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

    dom.$serviceListings.forEach(($listing) => {
      ScrollTrigger.create({
        trigger: $listing,
        start: 'top top+=106',
        end: 'bottom top+=106',
        once: false,
        autoKill: false,
        onToggle: (self) => {
          this.setActiveNavItem($listing.id);
        }
      });
    });
  }

  setActiveNavItem(targetId) {
    const { dom } = this;
    const $activeLink = dom.$component.querySelector(`button[data-anchor-id="${targetId}"]`);
    const $activeNavItem = $activeLink ? $activeLink.closest(this.refs.navItem) : null;
    
    // Update nav selected text
    if (dom.$navSelected && $activeLink) {
      dom.$navSelected.textContent = $activeLink.textContent.trim();
    }
    
    // Remove active class and aria-current from all nav items
    dom.$navItems.forEach($item => {
      $item.classList.remove('is-active');
    });
    dom.$navLinks.forEach($link => {
      $link.setAttribute('aria-current', 'false');
    });
    // Add active class and aria-current to current nav item
    if ($activeNavItem) {
      $activeNavItem.classList.add('is-active');
      if ($activeLink) $activeLink.setAttribute('aria-current', 'true');
    }
  }

  handleNavLinkClick(e) {
    const { dom, state } = this;

    const $btn = e.currentTarget;
    const targetId = $btn.dataset.anchorId;
    const offsetY = state.isMobile ? 104 : 67

    const $target = document.getElementById(targetId);

    gsap.to(window, {
      duration: .8,
      scrollTo: { y: `#${targetId}`, offsetY, autoKill: false },
      ease: "Power1.easeInOut",
      onComplete: () => {
        if ($target) $target.focus({ preventScroll: true });
      }
    });
  }

  toggleNav() {
    const { dom } = this;
    const isExpanded = dom.$navToggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeNav();
    } else {
      this.openNav();
    }
  }

  openNav() {
    const { dom, state } = this;
    const targetId = dom.$navToggle.getAttribute('aria-controls');
    const $targetList = document.getElementById(targetId);

    // Open nav items
    $targetList.style.height = `${$targetList.scrollHeight}px`;
    dom.$navToggle.setAttribute('aria-expanded', true);
    dom.$navToggleIcon.innerHTML = "-";

    state.accessibilityHelper.updateElementAccessibility($targetList, true);

    this.setScrollListener();
  }

  closeNav() {
    const { dom, state } = this;
    const targetId = dom.$navToggle.getAttribute('aria-controls');
    const $targetList = document.getElementById(targetId);

    // Close nav items
    $targetList.style.height = "0";
    dom.$navToggle.setAttribute('aria-expanded', false);
    dom.$navToggleIcon.innerHTML = "+";

    state.accessibilityHelper.updateElementAccessibility($targetList, false);
  }

  setScrollListener() {
    const { state } = this;
    window.addEventListener('scroll', this.closeNav.bind(this), { once: true });
  }

  handleAccordionClick(e) {
    const { refs, state } = this;
    const $el = e.currentTarget;
    const $button = $el.querySelector(refs.accordionToggle);
    const $icon = $el.querySelector(refs.accordionIcon);
    const $content = $el.querySelector(refs.accordionContent);
    const targetId = $button.getAttribute('aria-controls');
    const isExpanded = $button.getAttribute('aria-expanded') === 'true';
    const contentHeight = $content.scrollHeight;
    const targetList = document.getElementById(targetId);

    targetList.style.height = isExpanded ? "0" : `${contentHeight}px`;

    // Toggle aria-expanded
    $button.setAttribute('aria-expanded', !isExpanded);
    $icon.innerHTML = isExpanded ? "+" : "-";
    // Toggle active class for styling
    $el.classList.toggle('is-active');

    state.accessibilityHelper.updateElementAccessibility($content, !isExpanded);
  }
}

const $servicesListings = document.querySelector('.js-services-listings');
if ($servicesListings) new ServicesListings($servicesListings);